import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
} from '@mui/material';
import { toast } from 'react-toastify';
import { getSurveyStats, getSurveys } from '../../api/surveys';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { formatFailedActionMessage } from '../../utils/uiMessages';

const MetricCard = ({ title, value }) => (
  <Card variant="outlined">
    <CardContent>
      <Typography variant="caption" color="text.secondary">{title}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>{value}</Typography>
    </CardContent>
  </Card>
);

const ServiceQuality = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [surveys, setSurveys] = useState([]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, surveysRes] = await Promise.all([
        getSurveyStats(),
        getSurveys({ status: 'answered', maxScore: 6, limit: 20 }),
      ]);
      setStats(statsRes?.data || null);
      setSurveys(surveysRes?.data || []);
      setError('');
    } catch (err) {
      const message = getApiErrorMessage(err, formatFailedActionMessage('load', 'service quality'));
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        Service Quality (NPS/CSAT)
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard title="NPS" value={stats?.nps ?? 0} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard title="Response Rate" value={`${stats?.responseRate ?? 0}%`} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard title="Average Score" value={stats?.averageScore ?? 0} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard title="Unresolved Detractors" value={stats?.unresolvedDetractors ?? 0} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard title="Answered Surveys" value={stats?.totalAnswered ?? 0} />
            </Grid>
          </Grid>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Score Distribution
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Detractors: {stats?.distribution?.detractors || 0} | Passives: {stats?.distribution?.passives || 0} | Promoters: {stats?.distribution?.promoters || 0}
            </Typography>
          </Paper>

          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Answered At</TableCell>
                    <TableCell>Patient</TableCell>
                    <TableCell>Score</TableCell>
                    <TableCell>Channel</TableCell>
                    <TableCell>Comment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {surveys.map((survey) => (
                    <TableRow key={survey._id}>
                      <TableCell>{survey.answeredAt ? new Date(survey.answeredAt).toLocaleString() : '—'}</TableCell>
                      <TableCell>{survey.patient?.name || '—'}</TableCell>
                      <TableCell>
                        <Chip label={survey.score} size="small" color={survey.score <= 6 ? 'error' : 'default'} />
                      </TableCell>
                      <TableCell>{survey.channel}</TableCell>
                      <TableCell>{survey.comment || '—'}</TableCell>
                    </TableRow>
                  ))}
                  {surveys.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No detractor responses yet</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ServiceQuality;
